import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import http from "../services/http";
import { useAuth } from "../contexts/AuthContext";
import { useSnackbar } from "../contexts/SnackbarContext";
import AdminLayout from "./AdminLayout";

interface Artist {
  id: number;
  name: string;
  bio: string;
  avatarUrl: string;
}

interface Category {
  id: number;
  name: string;
  description: string;
  coverUrl: string;
}

const MusicForm: React.FC = () => {
  const { token } = useAuth();
  const { notify } = useSnackbar();
  const [title, setTitle] = useState("");
  const [releaseDate, setReleaseDate] = useState<Dayjs | null>(null);
  const [artistId, setArtistId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [musicFile, setMusicFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);

  // 加载艺术家和分类数据
  useEffect(() => {
    const loadData = async () => {
      if (!token) return;
      
      setLoading(true);
      try {
        const [artistsRes, categoriesRes] = await Promise.all([
          http.get('artist', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          http.get('category', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (artistsRes.data.code === "000000") {
          setArtists(artistsRes.data.data.artists);
        }
        if (categoriesRes.data.code === "000000") {
          setCategories(categoriesRes.data.data.categories);
        }
      } catch (err: any) {
        console.error("加载数据失败:", err);
        setError("加载数据失败，请刷新页面重试");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token]);

  // 生成图片预览
  useEffect(() => {
    if (!coverImage) return setCoverPreview(null);
    const url = URL.createObjectURL(coverImage);
    setCoverPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [coverImage]);

  // 计算音频时长
  const calculateDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      
      audio.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(url);
        resolve(audio.duration);
      });
      
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(url);
        reject(new Error('无法加载音频文件'));
      });
      
      audio.src = url;
    });
  };

  const handleFile =
    (setter: React.Dispatch<React.SetStateAction<File | null>>) =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setter(file);
      
      if (file && file.type.startsWith('audio/')) {
        try {
          const duration = await calculateDuration(file);
          setDuration(Math.round(duration));
        } catch (err) {
          console.error("计算音频时长失败:", err);
          setDuration(null);
        }
      }
    };

  // 提交表单 + 上传文件
  const handleSubmit = async () => {
    if (!musicFile || !coverImage || !title || !artistId || !categoryId) {
      setError("请填写所有必填字段");
      return;
    }

    if (!token) {
      notify("请先登录", "error");
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("releaseDate", releaseDate?.format("YYYY-MM-DD") || "");
    formData.append("artistId", artistId);
    formData.append("categoryId", categoryId);
    if (coverImage) {
      formData.append("coverFile", coverImage);
    }
    formData.append("audioFile", musicFile);
    if (duration) {
      formData.append("durationSeconds", duration.toString());
    }

    try {
      const res = await http.post(
        'music/upload',
        formData,
        {
          headers: { 
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`
          },
        }
      );
      
      if (res.data.code === "000000") {
        notify("上传成功", "success");
        // 重置表单
        setTitle("");
        setReleaseDate(null);
        setArtistId("");
        setCategoryId("");
        setCoverImage(null);
        setMusicFile(null);
        setDuration(null);
        setCoverPreview(null);
      } else {
        setError(res.data.message || "上传失败");
      }
    } catch (error: any) {
      console.error(error);
      setError("上传失败：" + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="上传音乐">
        <Box sx={{ maxWidth: 600, textAlign: "center", py: 4 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            加载数据中...
          </Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="上传音乐">
      <Box sx={{ maxWidth: 640 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

      <Stack spacing={2}>
        <TextField
          label="歌名"
          fullWidth
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        
        <DatePicker
          label="发行日"
          value={releaseDate}
          onChange={(newVal: Dayjs | null) => setReleaseDate(newVal)}
          slotProps={{ textField: { fullWidth: true } }}
        />
        
        <TextField
          select
          label="艺术家"
          fullWidth
          required
          value={artistId}
          onChange={(e) => setArtistId(e.target.value)}
        >
          {artists.map((artist) => (
            <MenuItem key={artist.id} value={artist.id.toString()}>
              {artist.name}
            </MenuItem>
          ))}
        </TextField>
        
        <TextField
          select
          label="分类"
          fullWidth
          required
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
        >
          {categories.map((category) => (
            <MenuItem key={category.id} value={category.id.toString()}>
              {category.name}
            </MenuItem>
          ))}
        </TextField>

        {/* 上传封面图片 */}
        <Box>
          <Button variant="outlined" component="label" fullWidth>
            上传封面图片
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleFile(setCoverImage)}
            />
          </Button>
          {coverPreview && (
            <Card sx={{ mt: 1 }}>
              <CardContent>
                <img
                  src={coverPreview}
                  alt="cover"
                  style={{ width: "100%", maxHeight: 200, objectFit: "cover" }}
                />
              </CardContent>
            </Card>
          )}
        </Box>

        {/* 上传音乐文件 */}
        <Box>
          <Button variant="outlined" component="label" fullWidth>
            上传音乐文件
            <input
              type="file"
              accept="audio/*"
              hidden
              onChange={handleFile(setMusicFile)}
            />
          </Button>
          {musicFile && (
            <Card sx={{ mt: 1 }}>
              <CardContent>
                <Typography variant="body2" gutterBottom>
                  <strong>文件名：</strong>{musicFile.name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>文件大小：</strong>{(musicFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                {duration && (
                  <Typography variant="body2" color="primary">
                    <strong>时长：</strong>{Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Box>

        {uploading && (
          <Box>
            <Typography variant="body2" gutterBottom>
              正在上传...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
          disabled={uploading || !title || !artistId || !categoryId || !musicFile || !coverImage}
        >
          {uploading ? "上传中..." : "提交音乐"}
        </Button>
      </Stack>
      </Box>
    </AdminLayout>
  );
};

export default MusicForm;
